import * as yup from 'yup';

/**
 * E37 · Check-in — the both-ends input codecs of the check-in mutations (plan 03 §2.1 #3): the phone (before it
 * queues a save), the portal and — as the shield's inputRule — the API run the SAME schema. Shape only; the
 * business rules (a rating's range, a day older than 7 days, an unknown emotion, who may remove an entry) are
 * the decider's, where they are binding. `subject`, `performer`, `practiceId` are ABSENT on purpose: the caller
 * is the performer, and their practice rides from the session — a client that could name them could write a
 * check-in into somebody else's record.
 *
 * `checkInId` is a UUIDv7 minted on the originator (the screen; the widget's offline queue) and IS the fact's
 * key, so the id's shape is checked at both ends.
 */
export const CHECK_IN_DRIVER_VALUES = [
  'work',
  'family',
  'relationships',
  'health',
  'sleep',
  'money',
  'study',
  'weather',
  'other',
] as const;

const uuidV7 = yup
  .string()
  .trim()
  .matches(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  )
  .required();

// SHAPE only: a whole number. Whether it is 1–5 is the decider's (CHECKIN_RATING_RANGE — the board's coded refusal,
// in the person's language), so the wire answers with the board's code instead of a generic validation error.
const rating = yup.number().integer();
const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const content = {
  checkInId: uuidV7,
  mood: rating.required(),
  emotions: yup
    .array()
    .of(
      yup
        .object({
          id: yup.number().integer().min(1).required(),
          strength: yup.number().integer().required(),
        })
        .required(),
    )
    .max(60)
    .nullable(),
  energy: rating.nullable(),
  sleepQuality: rating.nullable(),
  sleepHours: yup.number().nullable(),
  socialConnection: rating.nullable(),
  drivers: yup.array().of(yup.string().required()).max(20).nullable(),
  note: yup.string().max(4000).nullable(),
  recordedFor: yup
    .string()
    .trim()
    .matches(/^\d{4}-\d{2}-\d{2}$/)
    .required(),
};

export const recordCheckInInputSchema = yup.object({
  ...content,
  occurredAt: yup.date().nullable(),
  timeZone: yup.string().trim().max(64).nullable(),
});

export const recordCheckInInSessionInputSchema = yup.object({
  ...content,
  subject: userRef.required(),
});

export const deleteCheckInInputSchema = yup.object({
  checkInId: yup.string().trim().min(1).max(128).required(),
  subject: userRef.nullable(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const recordCheckInArgsSchema = yup.object({
  input: recordCheckInInputSchema.required(),
});
export const recordCheckInInSessionArgsSchema = yup.object({
  input: recordCheckInInSessionInputSchema.required(),
});
export const deleteCheckInArgsSchema = yup.object({
  input: deleteCheckInInputSchema.required(),
});

export const checkInValidators = {
  recordCheckInInputSchema,
  recordCheckInInSessionInputSchema,
  deleteCheckInInputSchema,
  recordCheckInArgsSchema,
  recordCheckInInSessionArgsSchema,
  deleteCheckInArgsSchema,
};
